import Vue from 'vue'
import Vuex from 'vuex'
import VuexPersistence from 'vuex-persist'

import axios from 'axios'
import CSRF from '@/services/csrf'

import User, { validateToken } from '@/services/users'

Vue.use(Vuex)

const vuexLocal = new VuexPersistence({
  storage: window.localStorage,
})

const state = {
  user: null,
  userLoggedInStatus: false,
  authToken: null,
}

const mutations = {
  SET_ONLINE_STATUS(state, payload) {
    state.userLoggedInStatus = payload.userLoggedInStatus
  },
  UPDATE_USER(state, payload) {
    state.user = payload.user
    state.authToken = payload.authToken || state.authToken
    state.userLoggedInStatus = true
  },
  // eslint-disable-next-line no-unused-vars
  LOGOUT_USER(state, payload) {
    state.user = null
    state.userLoggedInStatus = false
    state.authToken = null
  },
}

const actions = {
  /**
   * Send the auth token in the store to the API to ensure it is valid.
   *
   * @param {object} vuex - The default Vuex object passed to actions.
   *
   * @returns {boolean} - True/false the token in the store is valid.
   */
  validateToken({ state }) {
    if (!state.authToken) {
      return false
    }
    return validateToken(state.authToken).then(data => data.token_valid)
  },

  /**
   * Refresh the data of the current user in the store.
   *
   * @param {object} vuex - The default Vuex object passed to actions.
   *
   * @returns {Promise} - Promise object representing API call or `null`.
   */
  refreshCurrentUser({ state, commit }) {
    if (!state.user) {
      return null
    }

    return User.api
      .get(state.user.id)
      .then(user => {
        //console.log(user)
        commit('UPDATE_USER', { user })
        return user
      })
      .catch(() => {
        // do nothing for now
        return null
      })
  },

  /**
   * Check whether user auth is in a bad state, if so force the user to log out.
   *
   * @param {object} vuex - The default Vuex object passed to actions.
   * @param {object} payload - A custom payload for the action.
   * @param {object.object} payload.route - A Vue route object. The action checks
   *    whether this route requires token authentication. If yes, then it validates
   *    that a token exists and is recognized by the API. If either check fails, the
   *    user is forced to log out.
   *
   * @returns {boolean} - True/false the user was logged out.
   */
  checkShouldLogOut({ state, commit, dispatch }, { route }) {
    // If the route requires token validation, do it
    if (route.matched.some(r => r.meta.validateTokenOnRefresh)) {
      if (!state.authToken) {
        commit('LOGOUT_USER')
        return true
      }

      return dispatch('validateToken')
        .then(tokenValid => !tokenValid)
        .then(shouldLogOut => {
          if (shouldLogOut) {
            commit('LOGOUT_USER')
            return true
          }
        })
    }
    return false
  },
}

const plugins = [vuexLocal.plugin]

const getters = {
  apiClient: (state, getters) => {
    const { location } = window
    return axios.create({
      baseURL: `${location.protocol}//${location.host}/api/`,
      headers: {
        ...CSRF.getHeaders(),
        ...getters.getHeaders,
      },
    })
  },
  getHeaders: state => {
    return state.authToken ? { Authorization: `Token ${state.authToken}` } : {}
  },
}

export default new Vuex.Store({
  state,
  mutations,
  actions,
  getters,
  plugins,
})
